import {
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    PanResponder,
    Animated,
    Platform,
    View
} from "react-native";
import type ISheetPanelProps from "./type";
import type {
    SheetPanelWebPropsType,
    SheetPanelGestureType
} from "./type";
import stylesheet, {
    SHEET_GESTURE_FAST_VELOCITY,
    SHEET_GESTURE_SETTLE_FRAME,
    SHEET_GESTURE_CLOSE_RATIO,
    SHEET_PANEL_EDGE_STYLES,
    getSheetPanelAxis,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion,
    MOTION_SPRINGS
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSheetContext
} from "../../context";
import {
    SURFACE_CONTAINER_COLOR
} from "../../../../helpers/theme/elevation";
import {
    getDirectionSign
} from "../../../../helpers/direction";
import {
    getModalMotion
} from "../../../modal/stylesheet";

const SheetPanel: FC<ISheetPanelProps> = ({
    isFadeEnabled,
    direction,
    maxHeight,
    children,
    style,
    width,
    ...props
}) => {
    const sheet = useSheetContext({
        part: "Content"
    });

    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        durations,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(sheet?.customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const pointerType = usePointerType();

    const edge = sheet?.edge ?? "end";

    const isHorizontal = edge === "start" || edge === "end";

    const axisSign = getSheetPanelAxis({
        directionSign: getDirectionSign({
            direction
        }),
        edge
    });

    const isNativeDriver = Platform.OS !== "web";

    const [
        measuredSize,
        setMeasuredSize
    ] = useState<number | undefined>(undefined);

    const size = measuredSize ?? (isHorizontal ? width : maxHeight);

    const progressAnim = useRef(new Animated.Value(1)).current;

    const dragAnim = useRef(new Animated.Value(0)).current;

    const fadeAnim = useRef(new Animated.Value(isFadeEnabled ? 0 : 1)).current;

    const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const isDragClosingRef = useRef(false);

    const gesture = useRef<SheetPanelGestureType>({
        requestClose: () => undefined,
        isReduceMotionEnabled: false,
        isDragEnabled: false,
        dragThreshold: 0,
        isOpen: false,
        isHorizontal,
        axisSign,
        size
    });

    gesture.current = {
        isDragEnabled: pointerType === "coarse" && edge !== "bottom",
        requestClose: sheet?.requestClose ?? (() => undefined),
        dragThreshold: shapeConfigs.dragThreshold,
        isOpen: sheet?.isOpen ?? false,
        isReduceMotionEnabled,
        isHorizontal,
        axisSign,
        size
    };

    const currentMotion = getModalMotion({
        isReduceMotionEnabled,
        durations
    });

    const {
        surface: surfaceDynamicStyle,
        clip: clipDynamicStyle
    } = useStyles({
        shapeConfigs,
        shadowAlpha,
        elevations,
        maxHeight,
        radiuses,
        borders,
        colors,
        width,
        edge
    });

    const settleDrag = () => {
        isDragClosingRef.current = false;

        if(gesture.current.isReduceMotionEnabled) {
            dragAnim.setValue(0);

            return;
        }

        Animated.spring(dragAnim, {
            ...MOTION_SPRINGS.standard,
            useNativeDriver: isNativeDriver,
            toValue: 0
        }).start();
    };

    const panResponder = useRef(PanResponder.create({
        onPanResponderRelease: (_, gestureState) => {
            const current = gesture.current;

            const main = current.isHorizontal ? gestureState.dx : gestureState.dy;

            const velocity = current.isHorizontal ? gestureState.vx : gestureState.vy;

            const isClose = main * current.axisSign > current.size * SHEET_GESTURE_CLOSE_RATIO || velocity * current.axisSign > SHEET_GESTURE_FAST_VELOCITY;

            if(!isClose) {
                settleDrag();

                return;
            }

            isDragClosingRef.current = true;

            current.requestClose();

            clearTimeout(settleTimer.current);

            settleTimer.current = setTimeout(() => {
                if(gesture.current.isOpen) {
                    settleDrag();
                }
            }, currentMotion.closeMoveDuration + SHEET_GESTURE_SETTLE_FRAME);
        },
        onMoveShouldSetPanResponder: (_, gestureState) => {
            const current = gesture.current;

            if(!current.isDragEnabled || !current.isOpen) {
                return false;
            }

            const main = current.isHorizontal ? gestureState.dx : gestureState.dy;

            const cross = current.isHorizontal ? gestureState.dy : gestureState.dx;

            return Math.abs(main) > current.dragThreshold && Math.abs(main) > Math.abs(cross) && main * current.axisSign > 0;
        },
        onPanResponderMove: (_, gestureState) => {
            const current = gesture.current;

            const main = current.isHorizontal ? gestureState.dx : gestureState.dy;

            dragAnim.setValue(current.axisSign * Math.max(0, main * current.axisSign));
        },
        onPanResponderTerminate: () => {
            settleDrag();
        }
    })).current;

    useEffect(() => {
        if(!sheet) {
            return;
        }

        progressAnim.stopAnimation();

        fadeAnim.stopAnimation();

        if(isFadeEnabled) {
            Animated.timing(fadeAnim, {
                duration: sheet.isOpen ? currentMotion.openFadeDuration : currentMotion.closeFadeDuration,
                easing: sheet.isOpen ? currentMotion.openEasing : currentMotion.closeEasing,
                useNativeDriver: isNativeDriver,
                toValue: sheet.isOpen ? 1 : 0
            }).start();
        }

        if(sheet.isOpen) {
            dragAnim.setValue(0);

            if(currentMotion.openMoveDuration === 0) {
                progressAnim.setValue(0);

                return;
            }

            Animated.timing(progressAnim, {
                duration: currentMotion.openMoveDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 0
            }).start();

            return;
        }

        const isDragClosing = isDragClosingRef.current;

        isDragClosingRef.current = false;

        if(currentMotion.closeMoveDuration === 0) {
            return;
        }

        if(isDragClosing) {
            Animated.spring(progressAnim, {
                ...MOTION_SPRINGS.standard,
                useNativeDriver: isNativeDriver,
                toValue: 1
            }).start();

            return;
        }

        Animated.timing(progressAnim, {
            duration: currentMotion.closeMoveDuration,
            easing: currentMotion.closeEasing,
            useNativeDriver: isNativeDriver,
            toValue: 1
        }).start();
    }, [
        sheet?.isOpen
    ]);

    useEffect(() => {
        return () => {
            clearTimeout(settleTimer.current);

            progressAnim.stopAnimation();

            fadeAnim.stopAnimation();

            dragAnim.stopAnimation();
        };
    }, []);

    if(!sheet) {
        return null;
    }

    const onLayout = (event: LayoutChangeEvent) => {
        const {
            height: layoutHeight,
            width: layoutWidth
        } = event.nativeEvent.layout;

        setMeasuredSize(isHorizontal ? layoutWidth : layoutHeight);
    };

    const translate = Animated.add(progressAnim.interpolate({
        outputRange: [
            0,
            axisSign * size
        ],
        inputRange: [
            0,
            1
        ]
    }), dragAnim);

    const webProps: SheetPanelWebPropsType = Platform.OS === "web" ? {
        dir: direction
    } : {};

    return <Animated.View
        {...props}
        {...webProps}
        {...panResponder.panHandlers}
        style={[
            style,
            stylesheet.container,
            stylesheet[SHEET_PANEL_EDGE_STYLES[edge]],
            surfaceDynamicStyle,
            {
                transform: [
                    isHorizontal ? {
                        translateX: translate
                    } : {
                        translateY: translate
                    }
                ],
                opacity: fadeAnim
            }
        ]}
        onLayout={onLayout}
    >
        <View
            style={[
                stylesheet.clip,
                clipDynamicStyle
            ]}
        >
            <NCoreUIKitTheme.SurfaceProvider
                surfaceColor={SURFACE_CONTAINER_COLOR}
            >
                {children}
            </NCoreUIKitTheme.SurfaceProvider>
        </View>
    </Animated.View>;
};
export default SheetPanel;
